import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react"
import { v4 as uuidv4 } from 'uuid'
import './index.css'
import App from './App.jsx'
import { setupChunkErrorHandler } from './utils/lazyWithRetry'

// Setup global handler for chunk loading errors (stale deployments)
setupChunkErrorHandler();

// Polyfill for crypto.randomUUID if not available
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  crypto.randomUUID = () => uuidv4();
}

// Session Replay is the single most expensive thing this app did on mobile, and
// turning the sample rates down does NOT fix it: any replaysOnErrorSampleRate > 0
// puts rrweb in *buffering mode*, where it observes and serializes the DOM
// continuously for EVERY user so it always has the last ~60s ready to upload if an
// error shows up. The sample rate only decides whether that buffer gets sent.
//
// So the only way to stop paying the recording cost on warehouse phones is to not
// load the integration there at all. Desktop keeps it: the CPU headroom is there,
// and that's where replays actually get watched.
const isMobileDevice = typeof navigator !== 'undefined' && (
  navigator.userAgentData?.mobile ?? /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
);

// Only initialize Sentry if DSN is provided (will be empty in development)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(isMobileDevice ? [] : [Sentry.replayIntegration()]),
    ],
    // Was 1.0 (the wizard default): every single request produced a span that was
    // uploaded, on the hardware and connections least able to afford it.
    tracesSampleRate: 0.1,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Was 0.1 / 1.0 (wizard defaults). The old replaysOnErrorSampleRate: 1.0 meant
    // any user hitting errors had their whole session recorded AND uploaded — over
    // the very connection whose failure caused the error. That sampling selected
    // *against* the people who could least afford it: the worse the signal, the
    // more work the app did. Both are no-ops on mobile now (no integration), and
    // deliberately conservative on desktop.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isMobileDevice ? 0 : 0.1,
    release: import.meta.env.VITE_APP_VERSION,
    environment: import.meta.env.MODE, // 'development' or 'production'
    ignoreErrors: [
      /^Unable to preload CSS for/,
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      /Loading chunk [\d]+ failed/,
      /Loading CSS chunk [\d]+ failed/,
      /not a valid JavaScript MIME type/,
      'ChunkLoadError',
    ],
  });
}


createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
